import { Stack } from '@mui/material'
import { useTaskSideDrawer } from 'ComponentLibrary/src/context/TaskSideDrawerContext'
import { CreateNewTask } from 'ClientPortal/src/pages/Orders/OrderDetails/TaskList/CreateNewTask/CreateNewTask'
import { useTaskListPage } from 'ClientPortal/src/pages/Orders/OrderDetails/TaskList/TaskListPage.data'
import { OrderSidebar } from 'ClientPortal/src/pages/Orders/OrderSidebar'
import { STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { useFormQueryParams } from 'ClientPortal/src/router/useFormQueryParams'
import { assembleAssetTitle, BreadcrumbItem, Breadcrumbs, PageTemplate, TaskListWithDashboard, tokens } from 'ComponentLibrary'
import { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { TASK_STATUS_INVALIDATION_DELAY_IN_MS } from 'ClientPortal/src/api/Queries/tasks'
import { partialRight } from 'lodash'
import { getLatestUrlParams } from 'ComponentLibrary/src/hooks/useUrlState'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { useTranslation } from 'react-i18next'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { PlusCircleIcon } from '@phosphor-icons/react'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'

export function TaskListPage() {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.taskListPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const { orderOverview, isTasksPending, doneTasksWithCounts, openTasksWithCounts, toggleTaskStatus, users, changeTaskAssignee } =
        useTaskListPage()

    const { createMode, setCreateMode } = useFormQueryParams()
    const { openTaskSideDrawer } = useTaskSideDrawer()

    const assetTitle = assembleAssetTitle({
        customer_asset_reference: orderOverview?.customer_asset_reference,
        ...orderOverview?.address,
    })

    const title = assetTitle ? t('titleForAsset', { assetTitle }) : tTerminology('tasks')
    const breadCrumbs = [
        { title: t('allOrders'), link: STATIC_APP_PATHS.orderListPage, params: getLatestUrlParams('client-order-filter') },
        { title },
    ]
    const userLinkCopy = useUserLinkCopy()
    const taskListCopy = {
        open: t('dashboard.open'),
        completed: t('dashboard.completed'),
        noDeadline: t('dashboard.noDeadline'),
        deleteTask: t('dashboard.deleteTask'),
        deleteConfirm: t('dashboard.deleteConfirm'),
        deleteTitle: (taskTitle: string) => t('dashboard.deleteTitle', { taskTitle }),
        assigneeSearchPlaceholder: t('dashboard.assigneeSearchPlaceholder'),
    }

    return (
        <TaskListPageWrapper
            breadCrumbs={breadCrumbs}
            headline={
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={tokens.SizingSpacing200}
                    marginTop={tokens.SizingSpacing400}
                    marginBottom={tokens.SizingSpacing400}
                >
                    <Headline
                        title={title}
                        variant="headline1"
                        fullWidth={false}
                        iconButtons={[
                            {
                                icon: <PlusCircleIcon />,
                                onClick: () => setCreateMode(true),
                                tooltip: t('addTaskTooltip'),
                                ariaLabel: t('addTaskTooltip'),
                            },
                        ]}
                    />
                    {!isTasksPending && (
                        <Typography variant="textM" color={tokens.ColorTextDefault}>
                            {t('openTasks', { count: openTasksWithCounts.length })}
                        </Typography>
                    )}
                </Stack>
            }
        >
            <CreateNewTask isOpen={createMode} handleClose={() => setCreateMode(false)} orderTitle={assetTitle} />
            <TaskListWithDashboard
                isLoading={isTasksPending}
                showTaskVisibility={false} // Currently an appraiser only concept so appraisers can create tasks for appraisers
                tasksInDone={doneTasksWithCounts}
                tasksInOpen={openTasksWithCounts}
                onTaskClick={openTaskSideDrawer}
                toggleTaskStatus={partialRight(toggleTaskStatus, TASK_STATUS_INVALIDATION_DELAY_IN_MS)}
                assigneeOptions={users}
                onChangeAssignee={changeTaskAssignee}
                deleteTaskProps={undefined} // Only appraiser are currently allowed to delete tasks
                copy={taskListCopy}
                userLinkCopy={userLinkCopy}
            />
        </TaskListPageWrapper>
    )
}

export const TaskListPageWrapper = ({
    headline,
    breadCrumbs,
    children,
}: {
    children: ReactNode
    headline: ReactNode
    breadCrumbs: BreadcrumbItem[]
}) => {
    const { pathname } = useLocation()
    return (
        <Stack direction="row">
            <OrderSidebar />
            <PageTemplate
                withFooter
                fullWidth
                withMaxWidth
                alignLeft
                withBottomSpace
                pageTopAdornment={<Breadcrumbs items={breadCrumbs} currentLocation={pathname} />}
            >
                {headline}
                {children}
            </PageTemplate>
        </Stack>
    )
}
